import { useState, useEffect } from "react";
import {
  X, Wifi, WifiOff, Server, Wrench, ExternalLink,
  Zap, CheckCircle2, AlertCircle, Loader2
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  food: "bg-orange-100 text-orange-600",
  grocery: "bg-lime-100 text-lime-600",
  dining: "bg-violet-100 text-violet-600",
  transport: "bg-cyan-100 text-cyan-600",
  payments: "bg-blue-100 text-blue-600",
};

export default function MCPPanel({ onClose }) {
  const [servers, setServers] = useState([]);
  const [probing, setProbing] = useState(null);
  const [probeResults, setProbeResults] = useState({});

  useEffect(() => {
    loadServers();
  }, []);

  const loadServers = async () => {
    try {
      const res = await axios.get(`${API}/mcp/servers`);
      setServers(res.data.servers || []);
    } catch (e) {
      console.error("Failed to load MCP servers:", e);
    }
  };

  const probeServer = async (serverKey) => {
    setProbing(serverKey);
    try {
      const res = await axios.get(`${API}/mcp/servers/${serverKey}/probe`);
      setProbeResults((prev) => ({ ...prev, [serverKey]: res.data }));
    } catch (e) {
      setProbeResults((prev) => ({
        ...prev,
        [serverKey]: { reachable: false, error: "Connection failed" },
      }));
    } finally {
      setProbing(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="mcp-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-lg bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 shrink-0" style={{ backgroundColor: "#2D2766" }}>
          <div className="flex items-center gap-2">
            <Server className="w-5 h-5 text-white" />
            <h2 className="text-white font-semibold text-lg">Connected Services (MCP)</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10" data-testid="close-mcp-btn">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Info */}
        <div className="bg-teal-50 border-b border-teal-200 px-4 py-3">
          <p className="text-sm text-teal-800">
            These services are connected via <strong>MCP (Model Context Protocol)</strong>.
            When you chat about food, rides, or groceries, Elderra Agent routes your request to the right provider.
          </p>
        </div>

        {/* Servers list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {servers.map((server) => {
            const probe = probeResults[server.key];
            const catClass = CATEGORY_ICONS[server.category] || "bg-gray-100 text-gray-600";
            const isProbing = probing === server.key;

            return (
              <div
                key={server.key}
                className="bg-white border rounded-xl p-4"
                data-testid={`mcp-server-${server.key}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${catClass}`}>
                      <Zap className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{server.name}</h3>
                      <p className="text-xs text-gray-500">{server.description}</p>
                    </div>
                  </div>
                  <Badge className={`text-xs border-0 ${server.status === "available" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                    {server.status === "available" ? "Available" : server.status}
                  </Badge>
                </div>

                <div className="flex items-center gap-3 mt-3">
                  <Badge className="bg-gray-100 text-gray-600 border-0 text-xs capitalize">
                    {server.category}
                  </Badge>
                  <Badge className="bg-gray-100 text-gray-600 border-0 text-xs">
                    Auth: {server.auth_type}
                  </Badge>
                  {server.auth_configured && (
                    <Badge className="bg-green-100 text-green-700 border-0 text-xs flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Configured
                    </Badge>
                  )}
                  {server.has_url && (
                    <button
                      onClick={() => probeServer(server.key)}
                      disabled={isProbing}
                      className="ml-auto text-xs text-[#2D2766] hover:text-[#3D3580] font-medium flex items-center gap-1"
                      data-testid={`probe-${server.key}`}
                    >
                      {isProbing ? (
                        <><Loader2 className="w-3 h-3 animate-spin" /> Testing...</>
                      ) : (
                        <><Wifi className="w-3 h-3" /> Test Connection</>
                      )}
                    </button>
                  )}
                </div>

                {/* Probe result */}
                {probe && (
                  <div className={`mt-2 rounded-lg p-2.5 text-xs ${probe.reachable ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}>
                    {probe.reachable ? (
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium">Connected - {probe.tool_count} tools available</p>
                          {probe.tools && probe.tools.length > 0 && (
                            <p className="mt-1 text-[11px] opacity-80">
                              Tools: {probe.tools.slice(0, 5).join(", ")}
                              {probe.tools.length > 5 && `... +${probe.tools.length - 5} more`}
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        <p>{probe.error || "Connection failed"} — OAuth login may be required</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {servers.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <Server className="w-12 h-12 mx-auto mb-2" />
              <p>Loading service providers...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
